<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />
      <Toast />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <div class="mb-4 flex shrink-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Справочники</h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Системные справочники и классификаторы</p>
          </div>
        </div>

        <div class="mb-4 shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <div class="flex flex-wrap gap-2">
            <Button
              v-for="item in referenceTabs"
              :key="item.key"
              type="button"
              size="small"
              :severity="activeReference === item.key ? 'success' : 'secondary'"
              :outlined="activeReference !== item.key"
              class="!rounded-xl !px-3 !py-2"
              @click="switchReference(item.key)"
            >
              <div class="flex items-center gap-2">
                <i :class="[item.icon, 'text-sm']"></i>
                <span>{{ item.label }}</span>
                <span class="inline-flex min-w-6 items-center justify-center rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                  {{ getReferenceCount(item.key) }}
                </span>
              </div>
            </Button>
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <DataTable
            :value="filteredItems"
            :loading="loading"
            dataKey="id"
            removableSort
            showGridlines
            stripedRows
            size="small"
            scrollable
            scrollHeight="flex"
            class="reference-table h-full rounded-2xl"
          >
            <template #header>
              <div class="mb-4 flex flex-col gap-3">
                <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                    <div class="min-w-0 xl:max-w-md xl:flex-1">
                      <label class="mb-1 block text-sm font-medium text-slate-700">Поиск</label>
                      <IconField class="w-full">
                        <InputIcon>
                          <i class="pi pi-search" />
                        </InputIcon>
                        <InputText
                          v-model.trim="searchQuery"
                          class="w-full"
                          :placeholder="`Поиск в «${activeConfig.label}»...`"
                        />
                      </IconField>
                    </div>

                    <div class="flex flex-wrap items-end justify-end gap-2">
                      <Button
                        type="button"
                        icon="pi pi-filter-slash"
                        label="Очистить поиск"
                        variant="outlined"
                        severity="secondary"
                        class="h-11"
                        @click="searchQuery = ''"
                      />
                      <Button
                        type="button"
                        icon="pi pi-upload"
                        label="Экспорт CSV"
                        severity="info"
                        class="h-11"
                        @click="exportCurrentReference"
                      />
                    </div>
                  </div>
                </div>

                <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div class="grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                    <template v-if="activeReference === 'categories'">
                      <div class="grid gap-3 md:grid-cols-2">
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Класс товара</label>
                          <Select
                            v-model="categoryForm.classification_id"
                            :options="classifications"
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Выберите класс"
                            fluid
                          />
                        </div>
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Название категории</label>
                          <InputText
                            v-model.trim="categoryForm.name"
                            fluid
                            placeholder="Новое значение..."
                            @keydown.enter.prevent="createItem"
                          />
                        </div>
                      </div>
                    </template>

                    <template v-else-if="activeReference === 'counterparties'">
                      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Название</label>
                          <InputText
                            v-model.trim="counterpartyForm.name"
                            fluid
                            placeholder="Контрагент"
                            @keydown.enter.prevent="createItem"
                          />
                        </div>
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Телефон</label>
                          <InputMask
                            v-model.trim="counterpartyForm.phone"
                            mask="+7(999)999-99-99"
                            placeholder="+7(700)000-00-00"
                            fluid
                            class="reference-mask"
                          />
                        </div>
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Email</label>
                          <InputText
                            v-model.trim="counterpartyForm.email"
                            fluid
                            placeholder="Email"
                            @keydown.enter.prevent="createItem"
                          />
                        </div>
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Комментарий</label>
                          <InputText
                            v-model.trim="counterpartyForm.comment"
                            fluid
                            placeholder="Комментарий"
                            @keydown.enter.prevent="createItem"
                          />
                        </div>
                      </div>
                    </template>

                    <template v-else-if="activeReference === 'clientTypes'">
                      <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Название</label>
                          <InputText
                            v-model.trim="clientTypeForm.name"
                            fluid
                            placeholder="Новый тип клиента..."
                            @keydown.enter.prevent="createItem"
                          />
                        </div>

                        <div>
                          <label class="mb-1 block text-sm font-medium text-slate-700">Цвет</label>
                          <!-- <div class="rounded-2xl border border-slate-200 bg-white"> -->
                            <!-- <div
                              class="mb-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
                              :class="{ 'opacity-60': !clientTypeForm.color }"
                            >
                              <span
                                class="h-5 w-5 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                                :style="getColorSwatchStyle(clientTypeForm.color || defaultClientTypeColor)"
                              ></span>
                              <div class="min-w-0">
                                <p class="text-sm font-medium text-slate-700">
                                  {{ clientTypeForm.color ? getColorPaletteLabel(clientTypeForm.color) : 'Свободный цвет не выбран' }}
                                </p>
                                <p class="text-xs text-slate-400">
                                  {{ clientTypeForm.color || 'Нет доступных цветов' }}
                                </p>
                              </div>
                            </div> -->

                            <Select
                              v-model="clientTypeForm.color"
                              :options="clientTypeCreateColorOptions"
                              optionLabel="label"
                              optionValue="value"
                              optionDisabled="disabled"
                              placeholder="Выберите цвет"
                              fluid
                              :disabled="!hasAvailableClientTypeColors"
                            >
                              <template #value="slotProps">
                                <div v-if="slotProps.value" class="flex items-center gap-2">
                                  <span
                                    class="h-4 w-4 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                                    :style="getColorSwatchStyle(slotProps.value)"
                                  ></span>
                                  <span class="text-base text-slate-700">{{ getColorPaletteLabel(slotProps.value) }}</span>
                                  <span class="text-base text-slate-400">{{ slotProps.value }}</span>
                                </div>
                                <span v-else class="text-sm text-slate-400">Выберите цвет</span>
                              </template>

                              <template #option="{ option }">
                                <div class="flex items-center justify-between gap-3">
                                  <div class="flex items-center gap-2">
                                    <span
                                      class="h-4 w-4 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                                      :style="getColorSwatchStyle(option.value)"
                                    ></span>
                                    <div class="min-w-0">
                                      <p class="text-sm text-slate-700">{{ option.label }}</p>
                                      <p class="text-xs text-slate-400">{{ option.value }}</p>
                                    </div>
                                  </div>
                                  <span v-if="option.disabled" class="text-xs font-medium text-amber-600">
                                    Занят{{ option.ownerName ? `: ${option.ownerName}` : '' }}
                                  </span>
                                </div>
                              </template>
                            </Select>

                            <p v-if="!hasAvailableClientTypeColors" class="mt-2 text-xs text-amber-600">
                              Все цвета уже заняты. Освободите цвет в существующем типе клиента.
                            </p>
                          <!-- </div> -->
                        </div>
                      </div>
                    </template>

                    <template v-else>
                      <div class="max-w-xl">
                        <label class="mb-1 block text-sm font-medium text-slate-700">Значение</label>
                        <InputText
                          v-model.trim="simpleForm.name"
                          fluid
                          placeholder="Новое значение..."
                          @keydown.enter.prevent="createItem"
                        />
                      </div>
                    </template>

                    <Button
                      type="button"
                      icon="pi pi-plus"
                      label="Добавить"
                      severity="success"
                      class="h-11 xl:min-w-[10rem] xl:self-end"
                      :disabled="activeReference === 'clientTypes' && !hasAvailableClientTypeColors"
                      @click="createItem"
                    />
                  </div>
                </div>
              </div>
            </template>

            <template #empty>
              <div class="py-8 text-center text-slate-500">Записи не найдены</div>
            </template>

            <Column header="#" style="width: 5rem">
              <template #body="{ index }">
                <span class="text-slate-500">{{ index + 1 }}</span>
              </template>
            </Column>

            <Column
              v-for="column in activeColumns"
              :key="column.key"
              :field="column.key"
              :header="column.label"
              :style="getColumnStyle(column.key)"
            >
              <template #body="{ data }">
                <template v-if="isEditing(data.id)">
                  <Select
                    v-if="activeReference === 'categories' && column.key === 'classification'"
                    v-model="editForm.classification_id"
                    :options="classifications"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Выберите класс"
                    fluid
                  />

                  <InputText
                    v-else-if="activeReference === 'categories' && column.key === 'name'"
                    v-model.trim="editForm.name"
                    fluid
                  />

                  <InputText
                    v-else-if="activeReference === 'counterparties' && column.key === 'name'"
                    v-model.trim="editForm.name"
                    fluid
                  />

                  <InputMask
                    v-else-if="activeReference === 'counterparties' && column.key === 'phone'"
                    v-model.trim="editForm.phone"
                    mask="+7(999)999-99-99"
                    fluid
                    class="reference-mask"
                  />

                  <InputText
                    v-else-if="activeReference === 'counterparties' && column.key === 'email'"
                    v-model.trim="editForm.email"
                    fluid
                  />

                  <InputText
                    v-else-if="activeReference === 'counterparties' && column.key === 'comment'"
                    v-model.trim="editForm.comment"
                    fluid
                  />

                  <InputText
                    v-else-if="activeReference === 'clientTypes' && column.key === 'name'"
                    v-model.trim="editForm.name"
                    fluid
                  />

                  <Select
                    v-else-if="activeReference === 'clientTypes' && column.key === 'color'"
                    v-model="editForm.color"
                    :options="clientTypeEditColorOptions"
                    optionLabel="label"
                    optionValue="value"
                    optionDisabled="disabled"
                    fluid
                  >
                    <template #value="slotProps">
                      <div v-if="slotProps.value" class="flex items-center gap-2">
                        <span
                          class="h-4 w-4 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                          :style="getColorSwatchStyle(slotProps.value)"
                        ></span>
                        <span class="text-sm text-slate-700">{{ getColorPaletteLabel(slotProps.value) }}</span>
                      </div>
                      <span v-else class="text-sm text-slate-400">Выберите цвет</span>
                    </template>

                    <template #option="{ option }">
                      <div class="flex items-center justify-between gap-3">
                        <div class="flex items-center gap-2">
                          <span
                            class="h-4 w-4 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                            :style="getColorSwatchStyle(option.value)"
                          ></span>
                          <div class="min-w-0">
                            <p class="text-sm text-slate-700">{{ option.label }}</p>
                            <p class="text-xs text-slate-400">{{ option.value }}</p>
                          </div>
                        </div>
                        <span v-if="option.disabled" class="text-xs font-medium text-amber-600">
                          Занят{{ option.ownerName ? `: ${option.ownerName}` : '' }}
                        </span>
                      </div>
                    </template>
                  </Select>

                  <InputText
                    v-else
                    v-model.trim="editForm.name"
                    fluid
                  />
                </template>

                <template v-else>
                  <div
                    v-if="activeReference === 'clientTypes' && column.key === 'color'"
                    class="flex items-center gap-3"
                  >
                    <span
                      class="h-5 w-5 rounded-full border border-white shadow-sm ring-1 ring-slate-200"
                      :style="getColorSwatchStyle(data.color)"
                    ></span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-slate-700">{{ getColorPaletteLabel(data.color) }}</p>
                      <p class="text-xs text-slate-400">{{ normalizeColorValue(data.color) }}</p>
                    </div>
                  </div>

                  <span v-else class="text-slate-700">{{ displayValue(data, column.key) }}</span>
                </template>
              </template>
            </Column>

            <Column header="Действия" style="width: 8.5rem" class="!text-center">
              <template #body="{ data }">
                <div class="flex items-center justify-center gap-2">
                  <template v-if="isEditing(data.id)">
                    <Button icon="pi pi-check" variant="outlined" severity="success" @click="saveEdit(data.id)" />
                    <Button icon="pi pi-times" variant="outlined" severity="secondary" @click="cancelEdit" />
                  </template>

                  <template v-else>
                    <Button icon="pi pi-pencil" variant="outlined" severity="warn" @click="startEdit(data)" />
                    <Button icon="pi pi-trash" variant="outlined" severity="danger" @click="removeItem(data.id)" />
                  </template>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import axios from '../lib/axios'
import * as XLSX from 'xlsx'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()

const loading = ref(false)
const activeReference = ref('classifications')
const searchQuery = ref('')
const editingId = ref(null)

const classifications = ref([])
const categories = ref([])
const units = ref([])
const banks = ref([])
const currencies = ref([])
const clientTypes = ref([])
const counterparties = ref([])
const extraWorkReferences = ref([])

const simpleForm = reactive({ name: '' })
const clientTypePalette = [
  { label: 'Серый', value: '#64748B' },
  { label: 'Зеленый', value: '#10B981' },
  { label: 'Голубой', value: '#0EA5E9' },
  { label: 'Синий', value: '#3B82F6' },
  { label: 'Оранжевый', value: '#F97316' },
  { label: 'Желтый', value: '#F59E0B' },
  { label: 'Красный', value: '#EF4444' },
  { label: 'Розовый', value: '#EC4899' },
  { label: 'Бирюзовый', value: '#14B8A6' },
  { label: 'Лайм', value: '#84CC16' }
]
const defaultClientTypeColor = clientTypePalette[0].value
const clientTypeForm = reactive({ name: '', color: '' })
const categoryForm = reactive({ name: '', classification_id: '' })
const counterpartyForm = reactive({ name: '', phone: '', email: '', comment: '' })
const editForm = reactive({})

const referenceTabs = [
  { key: 'categories', label: 'Категории', icon: 'pi pi-folder' },
  { key: 'classifications', label: 'Классы товаров', icon: 'pi pi-tag' },
  { key: 'units', label: 'Единицы измерения', icon: 'pi pi-sliders-h' },
  { key: 'banks', label: 'Банки', icon: 'pi pi-building-columns' },
  { key: 'currencies', label: 'Валюты', icon: 'pi pi-dollar' },
  { key: 'clientTypes', label: 'Типы клиентов', icon: 'pi pi-id-card' },
  { key: 'counterparties', label: 'Контрагенты', icon: 'pi pi-users' },
  { key: 'extraWorkReferences', label: 'Доп. работа', icon: 'pi pi-briefcase' }
]

const referenceConfig = {
  categories: {
    label: 'Категории',
    endpoint: '/api/category',
    columns: [
      { key: 'classification', label: 'Класс товара' },
      { key: 'name', label: 'Значение' }
    ]
  },
  classifications: {
    label: 'Классы товаров',
    endpoint: '/api/classifications',
    columns: [{ key: 'name', label: 'Значение' }]
  },
  units: {
    label: 'Единицы измерения',
    endpoint: '/api/units',
    columns: [{ key: 'name', label: 'Значение' }]
  },
  banks: {
    label: 'Банки',
    endpoint: '/api/banks',
    columns: [{ key: 'name', label: 'Значение' }]
  },
  currencies: {
    label: 'Валюты',
    endpoint: '/api/currencies',
    columns: [{ key: 'name', label: 'Значение' }]
  },
  clientTypes: {
    label: 'Типы клиентов',
    endpoint: '/api/client-types',
    columns: [
      { key: 'name', label: 'Название' },
      { key: 'color', label: 'Цвет' }
    ]
  },
  counterparties: {
    label: 'Контрагенты',
    endpoint: '/api/counterparties',
    columns: [
      { key: 'name', label: 'Название' },
      { key: 'phone', label: 'Телефон' },
      { key: 'email', label: 'Email' },
      { key: 'comment', label: 'Комментарий' }
    ]
  },
  extraWorkReferences: {
    label: 'Доп. работа',
    endpoint: '/api/extra-work-references',
    columns: [{ key: 'name', label: 'Значение' }]
  }
}

const activeConfig = computed(() => referenceConfig[activeReference.value])
const activeColumns = computed(() => activeConfig.value.columns)

const itemsByReference = computed(() => ({
  categories: categories.value,
  classifications: classifications.value,
  units: units.value,
  banks: banks.value,
  currencies: currencies.value,
  clientTypes: clientTypes.value,
  counterparties: counterparties.value,
  extraWorkReferences: extraWorkReferences.value
}))

const filteredItems = computed(() => {
  const items = itemsByReference.value[activeReference.value] || []
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return items

  return items.filter((item) => {
    if (activeReference.value === 'counterparties') {
      return [item.name, item.phone, item.email, item.comment]
        .some((value) => String(value || '').toLowerCase().includes(q))
    }

    if (activeReference.value === 'categories') {
      return [item.name, item.classification]
        .some((value) => String(value || '').toLowerCase().includes(q))
    }

    if (activeReference.value === 'clientTypes') {
      return [item.name, item.color, getColorPaletteLabel(item.color)]
        .some((value) => String(value || '').toLowerCase().includes(q))
    }

    return String(item.name || '').toLowerCase().includes(q)
  })
})

const getReferenceCount = (key) => {
  return (itemsByReference.value[key] || []).length
}

const displayValue = (item, key) => {
  if (key === 'color') {
    return `${getColorPaletteLabel(item?.color)} (${normalizeColorValue(item?.color)})`
  }

  return item?.[key] || '—'
}

const getColumnStyle = (key) => {
  if (key === 'comment') return 'min-width: 18rem'
  if (key === 'color') return 'min-width: 16rem'
  if (key === 'name' || key === 'classification' || key === 'email') return 'min-width: 14rem'
  if (key === 'phone') return 'min-width: 13rem'
  return 'min-width: 12rem'
}

const normalize = (value) => String(value || '').trim().toLowerCase()
const asInputString = (value) => String(value ?? '')
const toColorValue = (value) => {
  const normalized = String(value || '').trim().toUpperCase()
  return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : ''
}
const normalizeColorValue = (value) => {
  return toColorValue(value) || defaultClientTypeColor
}
const getColorPaletteLabel = (value) => {
  return clientTypePalette.find((item) => item.value === normalizeColorValue(value))?.label || toColorValue(value) || 'Цвет'
}
const getColorSwatchStyle = (value) => ({
  backgroundColor: normalizeColorValue(value)
})
const buildClientTypeColorOptions = (currentColor = '', ignoreId = null) => {
  const normalizedCurrentColor = toColorValue(currentColor)
  const options = clientTypePalette.map((option) => {
    const owner = clientTypes.value.find((item) => {
      return Number(item.id) !== Number(ignoreId) && normalizeColorValue(item.color) === option.value
    })

    return {
      ...option,
      disabled: Boolean(owner),
      ownerName: owner?.name || ''
    }
  })

  if (normalizedCurrentColor && !options.some((option) => option.value === normalizedCurrentColor)) {
    options.unshift({
      label: `${normalizedCurrentColor} (текущий)`,
      value: normalizedCurrentColor,
      disabled: false,
      ownerName: ''
    })
  }

  return options
}
const clientTypeCreateColorOptions = computed(() => buildClientTypeColorOptions())
const clientTypeEditColorOptions = computed(() => buildClientTypeColorOptions(editForm.color, editingId.value))
const hasAvailableClientTypeColors = computed(() => {
  return clientTypeCreateColorOptions.value.some((option) => !option.disabled)
})
const getFirstAvailableClientTypeColor = (currentColor = '', ignoreId = null) => {
  return buildClientTypeColorOptions(currentColor, ignoreId).find((option) => !option.disabled)?.value || ''
}
const isClientTypeColorTaken = (value, ignoreId = null) => {
  const normalizedColor = toColorValue(value)

  if (!normalizedColor) {
    return false
  }

  return clientTypes.value.some((item) => {
    return Number(item.id) !== Number(ignoreId) && normalizeColorValue(item.color) === normalizedColor
  })
}
const syncClientTypeFormColor = () => {
  const currentColor = toColorValue(clientTypeForm.color)
  const currentOption = clientTypeCreateColorOptions.value.find((option) => option.value === currentColor)

  clientTypeForm.color = currentOption && !currentOption.disabled
    ? currentColor
    : getFirstAvailableClientTypeColor()
}

const resetFormState = () => {
  simpleForm.name = ''
  clientTypeForm.name = ''
  clientTypeForm.color = getFirstAvailableClientTypeColor()
  categoryForm.name = ''
  categoryForm.classification_id = ''
  counterpartyForm.name = ''
  counterpartyForm.phone = ''
  counterpartyForm.email = ''
  counterpartyForm.comment = ''
}

const clearEditForm = () => {
  Object.keys(editForm).forEach((key) => {
    delete editForm[key]
  })
}

const resetCurrentState = () => {
  searchQuery.value = ''
  editingId.value = null
  clearEditForm()
  resetFormState()
}

const switchReference = (key) => {
  activeReference.value = key
  resetCurrentState()
}

const fetchAll = async () => {
  loading.value = true
  try {
    const [categoryRes, classificationRes, unitRes, bankRes, currencyRes, clientTypeRes, counterpartyRes, extraWorkReferenceRes] = await Promise.all([
      axios.get('/api/category'),
      axios.get('/api/classifications'),
      axios.get('/api/units'),
      axios.get('/api/banks'),
      axios.get('/api/currencies'),
      axios.get('/api/client-types'),
      axios.get('/api/counterparties'),
      axios.get('/api/extra-work-references')
    ])

    categories.value = Array.isArray(categoryRes.data) ? categoryRes.data : []
    classifications.value = Array.isArray(classificationRes.data) ? classificationRes.data : []
    units.value = Array.isArray(unitRes.data) ? unitRes.data : []
    banks.value = Array.isArray(bankRes.data) ? bankRes.data : []
    currencies.value = Array.isArray(currencyRes.data) ? currencyRes.data : []
    clientTypes.value = Array.isArray(clientTypeRes.data) ? clientTypeRes.data : []
    counterparties.value = Array.isArray(counterpartyRes.data) ? counterpartyRes.data : []
    extraWorkReferences.value = Array.isArray(extraWorkReferenceRes.data) ? extraWorkReferenceRes.data : []
    syncClientTypeFormColor()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось загрузить справочники',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

const startEdit = (item) => {
  editingId.value = item.id
  clearEditForm()

  if (activeReference.value === 'counterparties') {
    editForm.name = item.name || ''
    editForm.phone = asInputString(item.phone)
    editForm.email = item.email || ''
    editForm.comment = item.comment || ''
    return
  }

  if (activeReference.value === 'categories') {
    editForm.name = item.name || ''
    editForm.classification_id = item.classification_id || ''
    return
  }

  if (activeReference.value === 'clientTypes') {
    editForm.name = item.name || ''
    editForm.color = toColorValue(item.color) || getFirstAvailableClientTypeColor('', item.id)
    return
  }

  editForm.name = item.name || ''
}

const cancelEdit = () => {
  editingId.value = null
  clearEditForm()
}

const isEditing = (id) => editingId.value === id

const saveEdit = async (id) => {
  try {
    if (activeReference.value === 'counterparties') {
      if (!editForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поля', detail: 'Название контрагента обязательно', life: 2500 })
        return
      }

      await axios.put(`/api/counterparties/${id}`, {
        name: editForm.name,
        phone: asInputString(editForm.phone),
        email: editForm.email,
        comment: editForm.comment
      })
    } else if (activeReference.value === 'categories') {
      if (!editForm.name || !editForm.classification_id) {
        toast.add({ severity: 'warn', summary: 'Проверьте поля', detail: 'Укажите класс и название категории', life: 2500 })
        return
      }

      await axios.put(`/api/category/${id}`, {
        name: editForm.name,
        classification_id: editForm.classification_id
      })
    } else if (activeReference.value === 'clientTypes') {
      if (!editForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Название типа клиента не может быть пустым', life: 2500 })
        return
      }

      if (!editForm.color) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Выберите цвет для типа клиента', life: 2500 })
        return
      }

      if (isClientTypeColorTaken(editForm.color, id)) {
        toast.add({ severity: 'warn', summary: 'Цвет занят', detail: 'Выберите другой цвет для типа клиента', life: 2500 })
        return
      }

      await axios.put(`${activeConfig.value.endpoint}/${id}`, {
        name: editForm.name,
        color: toColorValue(editForm.color)
      })
    } else {
      if (!editForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Значение не может быть пустым', life: 2500 })
        return
      }

      await axios.put(`${activeConfig.value.endpoint}/${id}`, { name: editForm.name })
    }

    cancelEdit()
    await fetchAll()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось сохранить запись',
      life: 3000
    })
  }
}

const createItem = async () => {
  try {
    if (activeReference.value === 'counterparties') {
      if (!counterpartyForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Название контрагента обязательно', life: 2500 })
        return
      }

      await axios.post('/api/counterparties', {
        name: counterpartyForm.name,
        phone: asInputString(counterpartyForm.phone),
        email: counterpartyForm.email,
        comment: counterpartyForm.comment
      })
    } else if (activeReference.value === 'categories') {
      if (!categoryForm.name || !categoryForm.classification_id) {
        toast.add({ severity: 'warn', summary: 'Проверьте поля', detail: 'Выберите класс и введите категорию', life: 2500 })
        return
      }

      await axios.post('/api/category', {
        name: categoryForm.name,
        classification_id: categoryForm.classification_id
      })
    } else if (activeReference.value === 'clientTypes') {
      if (!clientTypeForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Название типа клиента не может быть пустым', life: 2500 })
        return
      }

      if (!clientTypeForm.color) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Выберите свободный цвет для типа клиента', life: 2500 })
        return
      }

      if (isClientTypeColorTaken(clientTypeForm.color)) {
        toast.add({ severity: 'warn', summary: 'Цвет занят', detail: 'Выберите другой цвет для типа клиента', life: 2500 })
        syncClientTypeFormColor()
        return
      }

      await axios.post(activeConfig.value.endpoint, {
        name: clientTypeForm.name,
        color: toColorValue(clientTypeForm.color)
      })
    } else {
      if (!simpleForm.name) {
        toast.add({ severity: 'warn', summary: 'Проверьте поле', detail: 'Значение не может быть пустым', life: 2500 })
        return
      }

      await axios.post(activeConfig.value.endpoint, { name: simpleForm.name })
    }

    resetFormState()
    await fetchAll()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось добавить запись',
      life: 3000
    })
  }
}

const removeItem = async (id) => {
  try {
    await axios.delete(`${activeConfig.value.endpoint}/${id}`)
    if (editingId.value === id) cancelEdit()
    await fetchAll()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: getReferenceDeleteErrorMessage(error),
      life: 3000
    })
  }
}

const getReferenceDeleteErrorMessage = (error) => {
  const rawMessage = String(error?.response?.data?.message || '')
  const lowerMessage = rawMessage.toLowerCase()

  if (lowerMessage.includes('foreign key violation') || lowerMessage.includes('violates foreign key constraint')) {
    if (activeReference.value === 'categories') {
      return 'Нельзя удалить категорию, потому что она используется в товарах.'
    }

    if (activeReference.value === 'classifications') {
      return 'Нельзя удалить класс товара, потому что он используется в связанных записях.'
    }

    if (activeReference.value === 'units') {
      return 'Нельзя удалить единицу измерения, потому что она используется в связанных записях.'
    }

    if (activeReference.value === 'banks') {
      return 'Нельзя удалить банк, потому что он используется в связанных записях.'
    }

    if (activeReference.value === 'currencies') {
      return 'Нельзя удалить валюту, потому что она используется в связанных записях.'
    }

    if (activeReference.value === 'clientTypes') {
      return 'Нельзя удалить тип клиента, потому что он используется в связанных записях.'
    }

    if (activeReference.value === 'counterparties') {
      return 'Нельзя удалить контрагента, потому что он используется в связанных записях.'
    }

    if (activeReference.value === 'extraWorkReferences') {
      return 'Нельзя удалить вид доп. работы, потому что он используется в связанных записях.'
    }

    return 'Нельзя удалить запись, потому что она используется в других данных.'
  }

  return rawMessage || 'Не удалось удалить запись'
}

const exportCurrentReference = () => {
  if (!filteredItems.value.length) {
    toast.add({
      severity: 'warn',
      summary: 'Нет данных',
      detail: 'Нет записей для экспорта',
      life: 2500
    })
    return
  }

  const rows = filteredItems.value.map((item) => {
    const row = {}
    activeColumns.value.forEach((column) => {
      row[column.label] = displayValue(item, column.key)
    })
    return row
  })

  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Reference')
  XLSX.writeFile(workbook, `${activeReference.value}.csv`)
}

onMounted(fetchAll)
</script>

<style scoped>
:deep(.reference-table),
:deep(.reference-table .p-datatable-table-container) {
  height: 100%;
}

:deep(.reference-table .p-datatable-header) {
  border: 0;
  background: rgb(255 255 255);
  padding: 0;
}

:deep(.reference-table .p-datatable-table-container) {
  border: 1px solid rgb(226 232 240);
  border-radius: 1rem;
}

:deep(.reference-table .p-datatable-thead > tr > th) {
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgb(248 250 252);
  color: rgb(71 85 105);
  font-weight: 600;
  border-color: rgb(226 232 240);
  padding: 0.875rem 1rem;
}

:deep(.reference-table .p-datatable-tbody > tr > td) {
  border-color: rgb(241 245 249);
  color: rgb(51 65 85);
  padding: 0.875rem 1rem;
}

:deep(.reference-table .p-datatable-tbody > tr:hover > td) {
  background: rgba(248, 250, 252, 0.95);
}

:deep(.reference-table .p-datatable-footer) {
  background: rgb(255 255 255);
  color: rgb(51 65 85);
  border-top: 1px solid rgb(226 232 240);
  padding: 0.875rem 1rem;
}

:deep(.reference-mask .p-inputtext) {
  width: 100%;
}
</style>
